import type { StolpecPrimerjave } from '@koda/shared';
import type { JSX } from 'react';

import { evri } from '../lib/format';
import { useBesedila } from '../lib/jezik';
import { useTelefon } from '../lib/telefon';
import { evriSPredznakom } from './GlavaKazalnikov';
import { celicaDelov, sPredznakom } from './kazalniki-skupno';
import type { Celica } from './PanelRazclenitve';

interface Lastnosti {
  stolpci: StolpecPrimerjave[];
  leto: number;
  primerjalno: number | null;
  n: number;
  obdobje: string;
  naCelico: (celica: Celica) => void;
}

/** Puščica: gor zelena (boljši izid), dol rdeča (slabši). */
function Puscica({ gor }: { gor: boolean }): JSX.Element {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
      <path d={gor ? 'M6 2 L10.5 9 H1.5 Z' : 'M6 10 L10.5 3 H1.5 Z'} fill="currentColor" />
    </svg>
  );
}

/**
 * Primerjava do meseca n (točka 8): vodoraven trak petih stolpcev, ne tabela.
 * Letošnja vrednost velika, puščica z razliko in odstotkom, lanska manjša.
 * Stroški so negativni, zato pozitivna razlika vedno pomeni boljši izid
 * (zelena), tudi pri stroških. Na telefonu so stolpci vrstice.
 */
export function PrimerjavaDoMeseca({ stolpci, leto, primerjalno, n, obdobje, naCelico }: Lastnosti): JSX.Element {
  const b = useBesedila();
  const telefon = useTelefon();
  const naslov = b.pk.primerjavaDo(n);
  return (
    <section className="kz-karta kz-primerjava" aria-label={naslov}>
      <div className="kz-karta-naslov">
        <h2>{naslov}</h2>
        {!telefon && <div className="kz-karta-podnaslov">{b.pk.primerjavaPodnaslov(leto, primerjalno)}</div>}
      </div>
      <div className={telefon ? 'kz-primerjava-vrstice' : 'kz-primerjava-trak'}>
        {stolpci.map((s) => {
          const gor = s.razlika !== null && Number(s.razlika) >= 0;
          const zdaj = (
            <button
              type="button"
              className="kz-primerjava-zdaj"
              title={b.namigCelice}
              onClick={() => {
                naCelico(celicaDelov(s.naziv, obdobje, s.zdaj, s.deli));
              }}
            >
              {evri(s.zdaj)}
            </button>
          );
          const razlika =
            s.razlika === null ? null : (
              <span className={`kz-smer ${gor ? 'boljse' : 'slabse'}`}>
                <Puscica gor={gor} />
                {evriSPredznakom(s.razlika)}
                {!telefon && s.odstotek !== null && <span className="kz-tiho kz-normalno"> ({sPredznakom(s.odstotek)} %)</span>}
              </span>
            );
          const lani =
            s.lani === null || primerjalno === null ? null : (
              <button
                type="button"
                className="kz-tiho-gumb kz-primerjava-lani"
                title={b.namigCelice}
                onClick={() => {
                  naCelico(celicaDelov(s.naziv, String(primerjalno), s.lani!, s.deliLani));
                }}
              >
                {primerjalno}: {evri(s.lani)}
              </button>
            );
          return telefon ? (
            <div key={s.koda} className="kz-primerjava-vrstica">
              <div className="kz-primerjava-levo">
                <span>{s.naziv}</span>
                {lani}
              </div>
              <div className="kz-primerjava-desno">
                {zdaj}
                {razlika}
              </div>
            </div>
          ) : (
            <div key={s.koda} className="kz-primerjava-stolpec">
              <span className="kz-primerjava-naziv">{s.naziv}</span>
              {zdaj}
              {razlika}
              {lani}
            </div>
          );
        })}
      </div>
    </section>
  );
}
